import { FC, useState } from "react"
import { RotateCcw, ShieldAlert } from "lucide-react"
import ApproveDomainButton from "../approve-domain-button"
import DeclineDomainButton from "../decline-domain-button"
import { formatDateTimeFromTimestamp } from "../../utils"
import { usePendingEmbedDomains } from "../../hooks"

type Props = {
  integrationId?: string
}

/**
 * The blocked domains for one integration, each approvable in one click.
 *
 * This is the job the Activity Log below it could never really do: the log is
 * a flat, paged list of every request, where a rejected referrer is one row
 * among hundreds and approving it meant copying the URL into the Approved URLs
 * modal and remembering to append the wildcard.
 *
 * Domains the customer hides collapse into an undo list at the bottom — a hide
 * is a decision, not a delete, and it has to be reversible from the same place
 * it was made.
 *
 * Renders nothing when this integration has no blocked and no hidden domains.
 */
const BlockedDomainsCard: FC<Props> = ({ integrationId }) => {
  const {
    domains,
    approve,
    approvingHost,
    decline,
    decliningHost,
    declined,
    restore,
    restoringHost
  } = usePendingEmbedDomains({
    enabled: !!integrationId,
    integrationId
  })
  const [showDeclined, setShowDeclined] = useState(false)

  // The undo list is the only route back from a hide, so the card still has to
  // render when everything pending has been hidden.
  if (domains.length === 0 && declined.length === 0) return null

  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-6 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <ShieldAlert size={15} className="text-amber-500" aria-hidden="true" />
        <span className="text-sm font-medium text-amber-900">
          {domains.length === 0
            ? "No websites are waiting on a decision for this embed"
            : domains.length === 1
              ? "1 website is blocked from loading this embed"
              : `${domains.length} websites are blocked from loading this embed`}
        </span>
      </div>
      {domains.length > 0 && (
        <p className="mb-4 text-sm text-amber-800">
          These URLs are not on your approved list, so the embed will not load
          there. Approving a domain allows every page on it; hiding one leaves
          it blocked and just stops us asking.
        </p>
      )}

      <ul className="space-y-2">
        {domains.map(domain => (
          <li
            key={domain.host}
            className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white px-3 py-2.5"
          >
            <div className="min-w-0">
              <span className="block truncate text-sm font-medium text-base-dark">
                {domain.host}
              </span>
              <span className="block truncate text-xs text-neutral-500">
                {domain.hits === 1 ? "1 attempt" : `${domain.hits} attempts`},
                last{" "}
                {formatDateTimeFromTimestamp(
                  domain.last_seen,
                  "LL/dd/yyyy h:mm a"
                )}{" "}
                &middot; approves {domain.suggested_pattern}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ApproveDomainButton
                domain={domain}
                approve={approve}
                approvingHost={approvingHost}
                disabled={!!decliningHost}
              />
              <DeclineDomainButton
                domain={domain}
                decline={decline}
                decliningHost={decliningHost}
                disabled={!!approvingHost}
              />
            </div>
          </li>
        ))}
      </ul>

      {declined.length > 0 && (
        <div className="mt-4 border-t border-amber-200 pt-3">
          <button
            type="button"
            onClick={() => setShowDeclined(current => !current)}
            aria-expanded={showDeclined}
            className="text-xs font-medium text-amber-900 underline decoration-dashed underline-offset-4 hover:text-amber-950"
          >
            {declined.length === 1
              ? "1 hidden domain"
              : `${declined.length} hidden domains`}
            {showDeclined ? " — collapse" : " — show"}
          </button>

          {showDeclined && (
            <ul className="mt-2 space-y-2">
              {declined.map(domain => (
                <li
                  key={domain.host}
                  className="flex items-center justify-between gap-3 rounded border border-amber-200 bg-white/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <span className="block truncate text-sm text-neutral-600">
                      {domain.host}
                    </span>
                    <span className="block truncate text-xs text-neutral-500">
                      Still blocked &middot; hidden{" "}
                      {formatDateTimeFromTimestamp(
                        domain.declined_at,
                        "LL/dd/yyyy"
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={!!restoringHost}
                    onClick={() => restore(domain)}
                    title={`Show ${domain.host} again`}
                    className="inline-flex shrink-0 items-center text-xs font-medium text-amber-900 underline decoration-dashed underline-offset-4 hover:text-amber-950 disabled:no-underline disabled:opacity-50"
                  >
                    <RotateCcw size={12} className="mr-1" aria-hidden="true" />
                    {restoringHost === domain.host ? "Restoring…" : "Undo"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default BlockedDomainsCard
